import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboardStats, getOrders, getProducts, getSuppliers, getSaleVisibility, updateSaleVisibility } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Package, Users, Truck, ShoppingCart, Tag, ArrowRight, Calendar as CalendarIcon, Banknote, Copy, Check, MessageSquare, Eye, EyeOff } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { toast } from 'sonner';

const statusConfig = {
  pending: { label: '受付中', className: 'bg-amber-100 text-amber-800' },
  confirmed: { label: '確定', className: 'bg-blue-100 text-blue-800' },
  shipped: { label: '発送済', className: 'bg-purple-100 text-purple-800' },
  completed: { label: '完了', className: 'bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'キャンセル', className: 'bg-red-100 text-red-800' },
};

export const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saleDialogOpen, setSaleDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saleDate, setSaleDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [salePublished, setSalePublished] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes, productsRes, suppliersRes, visibilityRes] = await Promise.all([
        getDashboardStats(), 
        getOrders(),
        getProducts(),
        getSuppliers(),
        getSaleVisibility()
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.slice(0, 5));
      setSuppliers(suppliersRes.data);
      setSalePublished(visibilityRes.data.is_published);
      
      // 特売フラグがONかつ非表示ではない商品のみフィルタし、仕入れ先のsort_order順でソート
      const saleProducts = productsRes.data.filter(p => p.is_sale === true && p.is_hidden !== true);
      
      // 仕入れ先のsort_orderマップを作成
      const supplierSortMap = {};
      suppliersRes.data.forEach((sup, idx) => {
        supplierSortMap[sup.id] = sup.sort_order ?? idx;
      });
      
      // 仕入れ先のsort_order順でソート（仕入れ先なしは最後）
      saleProducts.sort((a, b) => {
        const aSort = a.supplier_id ? (supplierSortMap[a.supplier_id] ?? 9999) : 9999;
        const bSort = b.supplier_id ? (supplierSortMap[b.supplier_id] ?? 9999) : 9999;
        if (aSort !== bSort) return aSort - bSort;
        // 同じ仕入れ先内では元の順序（sort_order）を維持
        return (a.sort_order ?? 0) - (b.sort_order ?? 0);
      });
      
      setSales(saleProducts);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // 特売まとめテキストを生成（仕入れ先順だが名前は非表示）
  const generateSaleText = () => {
    const dateStr = saleDate 
      ? format(saleDate, 'M月d日(E)', { locale: ja })
      : '○月○日(○)';
    let text = `【${dateStr} 本日の特売】\n`;
    text += `※パック売り以外の商品はkg単価です。\n\n`;
    
    if (sales.length === 0) {
      text += '本日の特売商品はありません。';
    } else {
      sales.forEach((item, idx) => {
        text += `${idx + 1}. ${item.name}`;
        if (item.origin) text += `（${item.origin}）`;
        text += `\n`;
        // 価格が0の場合は「時価」と表示
        if (item.price === 0 || item.price === null || item.price === undefined) {
          text += `   時価`;
        } else {
          text += `   ${item.price?.toLocaleString()}円/${item.unit}`;
        }
        if (item.spec) text += ` [${item.spec}]`;
        text += `\n`;
        if (item.description) {
          text += `   ${item.description}\n`;
        }
        text += `\n`;
      });
      text += `ご注文お待ちしております！`;
    }
    
    return text;
  };

  // クリップボードにコピー
  const copyToClipboard = async () => {
    const text = generateSaleText();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('コピーしました！');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // フォールバック
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      toast.success('コピーしました！');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // 特売ページの公開/非公開を切り替え
  const toggleSaleVisibility = async () => {
    setPublishLoading(true);
    try {
      const newState = !salePublished;
      await updateSaleVisibility(newState);
      setSalePublished(newState);
      toast.success(newState ? '特売ページを公開しました' : '特売ページを非公開にしました');
    } catch (error) {
      console.error('Failed to toggle sale visibility:', error);
      toast.error('設定の更新に失敗しました');
    } finally {
      setPublishLoading(false);
    }
  };

  // 重要な統計（大きく表示）- 売上金額を上、取引先数を下
  const primaryStats = stats ? [
    { 
      label: 'やさまるFish売上金額', 
      value: `¥${stats.next_delivery_sales?.toLocaleString() || 0}`, 
      icon: Banknote, 
      color: 'text-emerald-600', 
      bgColor: 'bg-emerald-50', 
      link: '/orders',
      subtitle: '翌配送日分'
    },
    { 
      label: '本日の注文取引先', 
      value: stats.next_delivery_customers || 0, 
      icon: CalendarIcon, 
      color: 'text-amber-600', 
      bgColor: 'bg-amber-50', 
      link: '/orders',
      subtitle: '翌配送日分'
    },
  ] : [];

  // その他の統計（小さく表示）
  const secondaryStats = stats ? [
    { label: 'レギュラー商品', value: stats.total_products, icon: Package, color: 'text-slate-600', link: '/products' },
    { label: '特売商品', value: stats.total_sales, icon: Tag, color: 'text-red-600', link: '/sales' },
    { label: '取引先数', value: stats.total_customers, icon: Users, color: 'text-emerald-600', link: '/customers' },
    { label: '仕入れ先数', value: stats.total_suppliers, icon: Truck, color: 'text-purple-600', link: '/suppliers' },
  ] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8 fade-in" data-testid="dashboard">
      <div className="dashboard-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="dashboard-title">ダッシュボード</h1>
            <p className="dashboard-subtitle">発注管理システムの概要</p>
          </div>
          <div className="flex items-center gap-2">
            {/* 特売公開/非公開ボタン */}
            <Button 
              onClick={toggleSaleVisibility}
              disabled={publishLoading}
              size="sm"
              className={`text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3 ${salePublished 
                ? 'bg-emerald-600 hover:bg-emerald-700' 
                : 'bg-slate-500 hover:bg-slate-600'}`}
              data-testid="sale-visibility-btn"
            >
              {salePublished ? (
                <>
                  <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                  公開中
                </>
              ) : (
                <>
                  <EyeOff className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                  非公開
                </>
              )}
            </Button>
            <Button 
              onClick={() => setSaleDialogOpen(true)}
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
              data-testid="sale-summary-btn"
            >
              <Tag className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
              特売まとめ
            </Button>
          </div>
        </div>
      </div>

      {/* 特売まとめダイアログ */}
      <Dialog open={saleDialogOpen} onOpenChange={(open) => {
        setSaleDialogOpen(open);
        if (!open) setCalendarOpen(false); // ダイアログを閉じたらカレンダーも閉じる
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Tag className="h-5 w-5" />
              今日の特売まとめ
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* 日付選択 */}
            <div className="flex items-center gap-2 bg-amber-50 rounded-lg p-3">
              <CalendarIcon className="h-4 w-4 text-amber-600 shrink-0" />
              <span className="text-sm text-amber-800">日付:</span>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="h-8 border-amber-300 text-amber-800 hover:bg-amber-100"
                  >
                    {saleDate 
                      ? format(saleDate, 'M月d日(E)', { locale: ja })
                      : '日付を選択'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[100]" align="start" sideOffset={5}>
                  <Calendar
                    mode="single"
                    selected={saleDate}
                    onSelect={(date) => {
                      setSaleDate(date);
                      setCalendarOpen(false);
                    }}
                    locale={ja}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 max-h-80 overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm font-sans text-slate-700">
                {generateSaleText()}
              </pre>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={copyToClipboard} 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="copy-sale-btn"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    コピーしました！
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    テキストをコピー
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-500 text-center">
              <MessageSquare className="h-3 w-3 inline mr-1" />
              LINEやメッセージにそのまま貼り付けできます
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* 重要な統計（大きく表示） */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="primary-stats-grid">
        {primaryStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link key={index} to={stat.link}>
              <Card className={`hover:shadow-lg transition-shadow cursor-pointer border-2 ${stat.bgColor}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-base text-slate-600 font-medium">{stat.label}</p>
                      <p className="text-4xl md:text-5xl font-bold text-slate-900 mt-2">{stat.value}</p>
                      {stat.subtitle && <p className="text-xs text-slate-400 mt-1">{stat.subtitle}</p>}
                    </div>
                    <div className={`p-4 rounded-xl bg-white shadow-sm ${stat.color}`}>
                      <Icon className="h-10 w-10" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* その他の統計（小さく表示） */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="secondary-stats-grid">
        {secondaryStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Link key={index} to={stat.link}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">{stat.label}</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{stat.value}</p>
                    </div>
                    <div className={`p-2 rounded-lg bg-slate-100 ${stat.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <Card className="border-slate-200" data-testid="recent-orders-card">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-xl font-semibold">最近の注文</CardTitle>
          <Link to="/orders" className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
            すべて表示 <ArrowRight className="h-4 w-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {recentOrders.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <ShoppingCart className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>注文がありません</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-16">No.</th>
                    <th>取引先</th>
                    <th>金額</th>
                    <th>ステータス</th>
                    <th>日時</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order) => {
                    const status = statusConfig[order.status] || statusConfig.pending;
                    return (
                      <tr key={order.id}>
                        <td className="w-16">
                          <Link to={`/orders/${order.id}`} className="text-xs text-blue-600 hover:underline truncate block max-w-[60px]" title={order.order_number}>
                            {order.order_number.replace('ORD-', '').slice(-6)}
                          </Link>
                        </td>
                        <td>{order.customer_name || '-'}</td>
                        <td className="font-mono">¥{order.total_amount.toLocaleString()}</td>
                        <td><Badge className={status.className}>{status.label}</Badge></td>
                        <td className="text-slate-500">
                          {format(new Date(order.created_at), 'yyyy/MM/dd HH:mm', { locale: ja })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
